// Day 9 Day 2 followup — case-scoped operating AOI selection for restricted operators.
//
// Replaces the prior "auto-select operatingAois[0]" effect (App.jsx Item #6).
// Operating AOIs are now CASE-scoped: each PadRekko case owns its own AOI
// (computed at ingest from the union of pad envelopes). The restricted
// operator activates the AOI for whichever case they're working.
//
// Selection rules:
//   - 0 cases visible to the operator's org → no-op. The PadRekko view
//     should render an empty state (no active cases).
//   - 1 case → select it; set activeOperatingAoiId from
//     case.operating_aoi_id (may be null on legacy cases that pre-date
//     the case-scoped AOI work — handled by treating null as "no AOI",
//     same as no auto-select).
//   - >1 case → console.warn (a real product would show a case picker —
//     captured as backlog), then select the most recently-touched case.
//     The backend orders by updated_at DESC (NOT created_at) so
//     casesList[0] is the case the operator last worked — the right
//     "resume where I left off" semantic for a multi-case queue, rather
//     than pure creation order. casesList[0] is therefore the freshest.
//
// Guarded on isRestricted so non-restricted users (DH analysts, customer
// analysts) get nothing — they continue to drive operating-AOI selection
// through whatever UI path they normally use.
//
// One-time list fetch on mount; re-fetches when the restricted flag flips
// (e.g., demo prep flipping the column at runtime). Defensive against
// repeat-firing: once an active AOI is set, the effect short-circuits.

import { useEffect } from "react";

import { useAuthStore, selectIsRestrictedOperator } from "../stores/useAuthStore";
import { useOperatingAoiContext } from "../stores/useOperatingAoiContext";
import { useQcStore } from "../stores/useQcStore";

export function useRestrictedCaseSelection() {
  const isRestricted = useAuthStore(selectIsRestrictedOperator);
  const activeOperatingAoiId = useOperatingAoiContext((s) => s.activeOperatingAoiId);
  const setActiveOperatingAoiId = useOperatingAoiContext((s) => s.setActiveOperatingAoiId);

  const casesList = useQcStore((s) => s.casesList);
  const casesListLoading = useQcStore((s) => s.casesListLoading);
  const listCases = useQcStore((s) => s.listCases);

  // Kick the list fetch once when the restricted flag becomes true.
  useEffect(() => {
    if (!isRestricted) return;
    listCases();
  }, [isRestricted, listCases]);

  // Apply the selection rules once the list lands.
  useEffect(() => {
    if (!isRestricted) return;
    if (activeOperatingAoiId) return;
    if (casesListLoading) return;
    if (!casesList) return;

    if (casesList.length === 0) {
      console.warn(
        "[restricted-case-selection] No active cases for this operator org — PadRekko view will be empty."
      );
      return;
    }

    if (casesList.length > 1) {
      console.warn(
        "[restricted-case-selection] Multi-case state: operator has",
        casesList.length,
        "cases. Auto-selecting most recent; v1 has no case-picker UI."
      );
    }

    const chosen = casesList[0];
    if (!chosen.operating_aoi_id) {
      console.warn(
        "[restricted-case-selection] Selected case has no operating_aoi_id — map will show no clipped imagery.",
        { case_id: chosen.id, name: chosen.name }
      );
      return;
    }

    setActiveOperatingAoiId(chosen.operating_aoi_id);
  }, [
    isRestricted,
    activeOperatingAoiId,
    casesList,
    casesListLoading,
    setActiveOperatingAoiId,
  ]);
}
